import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

/**
 * Wraps every route in this app: redirects to `/login` (remembering where
 * the user was headed) when not signed in. The "user" role is kept on `/regulation` (HP Genie). Every role (reviewer, admin,
 * executive) can view the Dashboard - it's read-only oversight, not a
 * capability any of them should be blocked from - so there is no
 * role-gating here the way `@hp/workbench`'s Admin section has.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-sm text-slate-400">
        Checking your session...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // The "user" role (user@hindustanpower.com) sees HP Genie only: any other address goes to the chat.
  if (user.role === "user" && !location.pathname.startsWith("/regulation")) {
    return <Navigate to="/regulation" replace />;
  }

  return <>{children}</>;
}
